// The agents indicator in the top corner of every module page (Attract, Find, Network, Connect, ReConnect; 20 Oct 2026,
// docs/specs/MODULE_NAVIGATION.md § Agents indicator; steps E15 the engine view, 0.24 the engine's schedule). For one
// module: each part of the engine that does its work (engine_schedules via crm_engine_schedule, grouped by the hubs of
// engineHubs.ts), its state in one word, its last run, and what the owner can do when it is off or blocked:
//   * switch it on (set_engine_schedule, the owner's own action in act), or resume its paused area (set_engine_switch);
//   * when switching on needs set-up first (the module is Later or Off, or the playbook gives the part no setting), a
//     link to exactly that set-up step instead of a blind toggle;
//   * a "Fix it" link for a failing part (a disconnected mailbox, a refused key: agentHealth.failureFix).
// Each part also shows its next run, unless it runs all the time (every few minutes) (#364). Find's parts are grouped by
// what they do: Finding, Researching, Checking, Drafting, Sending (#365). The whole module can be paused or turned on
// again from the dropdown, with the same engine switches as Settings › Modules (set_engine_switch on each of the
// module's areas, #366). "Last run" is when the part last did work: the engine no longer counts a skipped or idle
// tick (engine/schedules.py, migration 20270121090000; #363).
// Pure functions, so the states and actions are tested on their own. Read by Settings › Automations and the spine's health line (components/SpineHealth.tsx).
import type { Crm, EngineSwitch } from "./crm";
import { agentBlockers, failureFix, type Blocker } from "./agentHealth";
import { contactFinderStats } from "./contactFinders";
import { fmtDayRelative, fmtTime } from "./format";
import { HUBS, type HubId } from "./engineHubs";
import { describeCadence, hubSwitchKey, partKey, partName, PART_TEXT, startingPoint, type CadenceSettings, type EnginePart, type EngineSchedule } from "./engineSchedule";
import type { ModuleKey } from "./setupCatalogue";
import type { SetupView } from "./setupFlow";

/** The left menu's section key for each module (Connect lives at /pipeline). */
export const SECTION_MODULE: Record<string, ModuleKey> = { attract: "attract", campaigns: "find", network: "network", conversations: "connect", reconnect: "reconnect" };

export const MODULE_LABEL: Record<ModuleKey, string> = { attract: "Attract", find: "Find", network: "Network", connect: "Connect", reconnect: "ReConnect" };

/** The hubs of the engine map whose parts do each module's work (engineHubs.ts `module`). */
export function moduleHubs(module: ModuleKey): HubId[] {
  return HUBS.filter((h) => h.module === module).map((h) => h.id);
}

/**
 * Parts that also do a module's work although their hub is another module's. Network has no hub of its own in the
 * engine: keeping in touch with introducers and finding events are the relationship agent's (ReConnect's hub).
 */
export const EXTRA_PARTS: Partial<Record<ModuleKey, string[]>> = {
  network: ["relationship-agent/daily", "relationship-agent/weekly"],
};

/**
 * What each of a module's parts does, in the order the work flows (#365). Find: finding firms, researching them
 * (people, checked email addresses, the research), checking for firms and people already in the CRM, drafting the
 * outreach, and sending it. A part not listed here goes in a last group, "Other", so nothing is ever hidden.
 */
export const MODULE_STAGES: Partial<Record<ModuleKey, { stage: string; parts: string[] }[]>> = {
  find: [
    { stage: "Finding", parts: ["sourcing-agent/daily"] },
    { stage: "Researching", parts: ["enrichment-agent/daily"] },
    { stage: "Checking", parts: ["enrichment-agent/weekly"] },
    { stage: "Drafting", parts: ["outreach-agent/draft", "outreach-agent/daily"] },
    { stage: "Sending", parts: ["outreach-agent/send"] },
  ],
};

export interface AgentGroup {
  /** The stage's name ("Finding"), or null for a module whose parts are not grouped. */
  stage: string | null;
  rows: AgentRow[];
}

/** The pause or turn-on for the whole module (#366): set_engine_switch on each key. */
export interface ModuleSwitch {
  kind: "pause" | "resume";
  label: string;
  keys: string[];
}

export type AgentWord = "Working" | "Ran today" | "On" | "Not run" | "Failing" | "Off" | "Paused";
export type AgentTone = "live" | "ok" | "warn" | "bad" | "muted";

export type AgentAction =
  /** set_engine_schedule with these settings (enabled: true, plus a starting cadence for a part with no schedule). */
  | { kind: "switch_on"; label: "Switch on"; agent: string; job: string; settings: Partial<CadenceSettings> & { enabled: true } }
  /** set_engine_switch: resume the paused area of the engine (key is the hub). */
  | { kind: "resume"; label: string; key: string }
  /** A page: the set-up step that must come first, or the page that fixes a blocker. */
  | { kind: "setup" | "fix"; label: string; to: string; why: string };

export interface AgentRow {
  key: string;
  agent: string;
  job: string;
  hub: HubId;
  name: string;
  does: string;
  word: AgentWord;
  tone: AgentTone;
  last_run_at: string | null;
  next_run_at: string | null;
  /** "Every 5 minutes" for a part that runs all the time (cadence every), else null: its next run is shown instead. */
  always: string | null;
  action: AgentAction | null;
}

export interface ModuleAgents {
  module: ModuleKey;
  /** Set-up's choice for the module ("later" and "off" switch its parts off); null when it could not be read. */
  state: "on" | "off" | "later" | null;
  rows: AgentRow[];
  /** The rows by stage (Find's five), or one group with no stage for the other modules. */
  groups: AgentGroup[];
  /** The engine switches of the module's areas (Settings › Modules pauses them), and those paused now. */
  switchKeys: string[];
  pausedKeys: string[];
  /** Blockers outside the parts themselves (Find: no mailbox, a missing finder key, a cap), from agentBlockers. */
  blockers: Blocker[];
  /** Parts switched on and not failing. */
  on: number;
  /** Parts running right now. */
  running: number;
  /** Failing parts plus blockers that stop work. */
  needs: number;
}

const isToday = (iso: string | null | undefined, now: Date) => {
  if (!iso) return false;
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};

/** Where a module is switched on or off in set-up. */
export const SETUP_MODULES_PATH = "/setup/modules";
export const setupPartsPath = (m: ModuleKey) => `/setup/parts/${m}`;
/** Settings › Automations, the engine's report (#478); Settings › Modules switches and schedules (#470). */
export const ENGINE_PATH = "/settings/automations";
export const MODULES_PATH = "/settings/modules";

/** Parts that cannot do their work without a mailbox ready to send (Find's sending). */
export const NEEDS_MAILBOX = new Set(["outreach-agent/daily", "outreach-agent/send"]);
/** Find's blockers that mean no email can go out at all. */
const NO_MAILBOX = new Set(["mailbox-none", "mailbox-not-ready"]);

/**
 * One part's state and the owner's next step. Switch on is offered only when it can work: set-up has the module on
 * (unknown set-up sends the owner to Your Modules instead), and a sending part has a mailbox ready (else the link to
 * connect one).
 */
export function agentRow(p: EnginePart, module: ModuleKey, paused: ReadonlySet<string>, state: ModuleAgents["state"], now = new Date(), blockers: Blocker[] = []): AgentRow {
  const name = partName(p);
  const base = {
    key: partKey(p), agent: p.agent, job: p.job, hub: p.hub, name, does: PART_TEXT[partKey(p)]?.does ?? "",
    last_run_at: p.last_run_at ?? null, next_run_at: p.enabled ? p.next_run_at ?? null : null,
    always: p.enabled && p.scheduled && p.cadence === "every" ? describeCadence(p) : null,
  };
  const label = MODULE_LABEL[module];
  if (state === "off" || state === "later") {
    return { ...base, word: "Off", tone: "muted", next_run_at: null, always: null,
      action: { kind: "setup", label: `Switch ${label} on in set-up`, to: SETUP_MODULES_PATH, why: `${label} is ${state === "later" ? "set to Later" : "off"} in set-up, so its agents do not run.` } };
  }
  if (!p.scheduled || !p.enabled) {
    if (state === null) {
      return { ...base, word: "Off", tone: "muted",
        action: { kind: "setup", label: "Open Your Modules", to: MODULES_PATH, why: "Couldn't check set-up just now." } };
    }
    const noMailbox = NEEDS_MAILBOX.has(partKey(p)) ? blockers.find((b) => NO_MAILBOX.has(b.key)) : undefined;
    if (noMailbox) {
      return { ...base, word: "Off", tone: "muted", action: { kind: "setup", label: noMailbox.fix, to: noMailbox.to, why: noMailbox.text } };
    }
    if (p.scheduled || p.recommended) {
      // A part with no schedule yet starts on its recommended setting, as Settings › Modules does.
      const settings = p.scheduled ? {} : startingPoint(p.recommended?.cadence ?? "daily", p.recommended ?? {});
      return { ...base, word: "Off", tone: "muted", action: { kind: "switch_on", label: "Switch on", agent: p.agent, job: p.job, settings: { ...settings, enabled: true } } };
    }
    return { ...base, word: "Off", tone: "muted",
      action: { kind: "setup", label: "Set it up first", to: setupPartsPath(module), why: `Your playbook does not use ${name.toLowerCase()} yet.` } };
  }
  const hubKey = hubSwitchKey(p.hub);
  if (hubKey && paused.has(hubKey)) {
    const hub = HUBS.find((h) => h.id === p.hub)?.name ?? "this area";
    return { ...base, word: "Paused", tone: "warn", next_run_at: null, always: null, action: { kind: "resume", label: `Resume ${hub}`, key: hubKey } };
  }
  if (p.running || p.run_requested) return { ...base, word: "Working", tone: "live", action: null };
  if (p.last_status === "failed" || p.last_status === "timeout") {
    const f = failureFix(p.last_error ?? p.last_summary);
    return { ...base, word: "Failing", tone: "bad", action: { kind: "fix", label: "Fix it", to: f.to, why: f.fix } };
  }
  if (isToday(p.last_run_at, now)) return { ...base, word: "Ran today", tone: "ok", action: null };
  if (p.last_run_at) return { ...base, word: "On", tone: "ok", action: null };
  return { ...base, word: "Not run", tone: "warn", action: null };
}

/** The module's parts, in the engine map's order, with their states. */
export function moduleAgents(
  module: ModuleKey,
  schedule: Pick<EngineSchedule, "parts"> | null,
  paused: ReadonlySet<string>,
  setup: Pick<SetupView, "modules" | "legacy"> | null,
  blockers: Blocker[] = [],
  now = new Date(),
): ModuleAgents {
  const hubs = moduleHubs(module);
  const extra = new Set(EXTRA_PARTS[module] ?? []);
  const parts = (schedule?.parts ?? []).filter((p) => hubs.includes(p.hub) || extra.has(partKey(p)));
  parts.sort((a, b) => order(a) - order(b));
  const state = setup ? (setup.legacy ? "on" : setup.modules?.[module]?.state ?? null) : null;
  // A module switched off in set-up has nothing that needs the owner here: its blockers wait until it is on again.
  const live = state === "off" || state === "later" ? [] : blockers;
  const rows = parts.map((p) => agentRow(p, module, paused, state, now, live));
  const on = rows.filter((r) => r.tone === "live" || r.tone === "ok" || r.word === "Not run").length;
  const running = rows.filter((r) => r.word === "Working").length;
  const needs = rows.filter((r) => r.word === "Failing").length + live.filter((b) => b.tone === "bad").length;
  // The areas Settings › Modules pauses: the module's own hubs that have parts (Network's parts are the
  // relationship agent's, in ReConnect's area, so Network has none of its own).
  const switchKeys = hubs.filter((h) => parts.some((p) => p.hub === h)).map(hubSwitchKey).filter((k): k is string => k !== null);
  const pausedKeys = switchKeys.filter((k) => paused.has(k));
  return { module, state, rows, groups: groupRows(module, rows), switchKeys, pausedKeys, blockers: live, on, running, needs };

  function order(p: EnginePart): number {
    const h = HUBS.findIndex((x) => x.id === p.hub);
    return h < 0 ? 99 : h;
  }
}

/** The module's rows by stage, in the stages' order; a module with no stages has one group. */
export function groupRows(module: ModuleKey, rows: AgentRow[]): AgentGroup[] {
  const stages = MODULE_STAGES[module];
  if (!stages) return rows.length ? [{ stage: null, rows }] : [];
  const groups: AgentGroup[] = stages.map((s) => ({
    stage: s.stage,
    rows: s.parts.map((k) => rows.find((r) => r.key === k)).filter((r): r is AgentRow => Boolean(r)),
  }));
  const listed = new Set(stages.flatMap((s) => s.parts));
  const other = rows.filter((r) => !listed.has(r.key));
  if (other.length) groups.push({ stage: "Other", rows: other });
  return groups.filter((g) => g.rows.length > 0);
}

/**
 * Pause or turn on the whole module from the dropdown (#366), as Settings › Modules does: "Turn on
 * Find" resumes every paused area of Find, "Pause Find" pauses them all. None when set-up has the module off or Later
 * (set-up switches it on, not the engine), or for a module with no area of its own or no parts.
 */
export function moduleSwitch(m: Pick<ModuleAgents, "module" | "state" | "switchKeys" | "pausedKeys" | "rows">): ModuleSwitch | null {
  if (!m.switchKeys.length || m.state === "off" || m.state === "later" || !m.rows.length) return null;
  const label = MODULE_LABEL[m.module];
  if (m.pausedKeys.length) return { kind: "resume", label: `Turn on ${label}`, keys: m.pausedKeys };
  return { kind: "pause", label: `Pause ${label}`, keys: m.switchKeys };
}

/** The closed pill: "Agents: 1 needs you" (ochre), "Agents: 3 working" (moss) or "Agents: Off" (muted). */
export function agentsPill(m: Pick<ModuleAgents, "needs" | "on" | "running">): { text: string; tone: "ochre" | "moss" | "muted"; live: boolean } {
  const live = m.running > 0;
  if (m.needs > 0) return { text: `${m.needs} ${m.needs === 1 ? "needs" : "need"} you`, tone: "ochre", live };
  if (m.on > 0) return { text: `${m.on} working`, tone: "moss", live };
  return { text: "Off", tone: "muted", live: false };
}

/**
 * Everything the indicator reads, each read on its own so one failure never hides the rest: the engine's schedule
 * (crm_engine_schedule), the paused areas (crm_engine_switches), set-up's module choices (crm_setup_state) and, for Find,
 * the blockers outside the parts (no mailbox, a missing finder key, a cap: agentBlockers). `readable` is false when the
 * schedule itself could not be read.
 */
export async function loadModuleAgents(crm: Crm, module: ModuleKey, opts: { fresh?: boolean; now?: Date } = {}): Promise<ModuleAgents & { readable: boolean }> {
  const [{ schedule, switches, setup }, blockers] = await Promise.all([
    cached(crm, "base", !!opts.fresh, async () => {
      const [schedule, switches, setup] = await Promise.all([
        crm.engineSchedule().catch(() => null),
        crm.engineSwitches().catch(() => []),
        crm.setupState().catch(() => null),
      ]);
      return { schedule, switches, setup };
    }),
    module === "find" ? cached(crm, "find", !!opts.fresh, () => findBlockers(crm)) : Promise.resolve([] as Blocker[]),
  ]);
  const paused = new Set((switches ?? []).filter((s) => s.paused).map((s) => s.key));
  return { ...moduleAgents(module, schedule, paused, setup, blockers, opts.now ?? new Date()), readable: schedule !== null };
}

/**
 * The indicator shows on every module page, so its reads are shared by all five modules and kept for a short while:
 * moving between pages and modules reuses them; opening the indicator, an action of its own or a task decided anywhere
 * reads afresh. A read that failed is not kept.
 */
export const AGENTS_TTL_MS = 60_000;
const CACHE = new WeakMap<Crm, Map<string, { at: number; value: Promise<unknown> }>>();

function cached<T>(crm: Crm, key: string, fresh: boolean, load: () => Promise<T>): Promise<T> {
  let m = CACHE.get(crm);
  if (!m) CACHE.set(crm, (m = new Map()));
  const hit = m.get(key);
  if (!fresh && hit && Date.now() - hit.at < AGENTS_TTL_MS) return hit.value as Promise<T>;
  const value = load();
  const entry = { at: Date.now(), value };
  m.set(key, entry);
  // Unreadable engine: try again next time rather than show "unknown" for a minute.
  void value.then((v) => {
    if ((v as { schedule?: unknown })?.schedule === null && m!.get(key) === entry) m!.delete(key);
  }, () => m!.get(key) === entry && m!.delete(key));
  return value;
}

/** Forget the shared reads (for tests, and when the business changes). */
export function forgetModuleAgents(crm: Crm): void {
  CACHE.delete(crm);
}

export async function findBlockers(crm: Crm): Promise<Blocker[]> {
  const [mail, finders] = await Promise.all([crm.mailboxHealth().catch(() => null), contactFinderStats(crm).catch(() => null)]);
  // The parts' own states come from the schedule; here only what blocks them from outside.
  return agentBlockers(null, mail, finders).filter((b) => b.kind === "mailbox" || b.kind === "key" || b.kind === "cap");
}

/** The chip each state's word wears. */
export const AGENT_CHIP: Record<AgentTone, string> = { live: "chip chip-ok", ok: "chip chip-ok", warn: "chip chip-warn", bad: "chip chip-bad", muted: "chip" };

/** Every module's parts and states from one read of each, for Settings › Automations and Settings › Modules. */
export interface EngineReport {
  /** null when the engine could not be read. */
  schedule: EngineSchedule | null;
  /** The engine switches as read (crm.engineSwitches), and the keys paused now. */
  switches: EngineSwitch[];
  paused: ReadonlySet<string>;
  modules: Record<ModuleKey, ModuleAgents>;
}

export async function loadEngineReport(crm: Crm, now = new Date()): Promise<EngineReport> {
  const [schedule, switches, setup, blockers] = await Promise.all([
    crm.engineSchedule().catch(() => null),
    crm.engineSwitches().catch(() => [] as EngineSwitch[]),
    crm.setupState().catch(() => null),
    findBlockers(crm).catch(() => [] as Blocker[]),
  ]);
  const paused = new Set(switches.filter((s) => s.paused).map((s) => s.key));
  const of = (m: ModuleKey) => moduleAgents(m, schedule, paused, setup, m === "find" ? blockers : [], now);
  return { schedule, switches, paused, modules: { attract: of("attract"), find: of("find"), network: of("network"), connect: of("connect"), reconnect: of("reconnect") } };
}

/** The parts no module owns: your CRM and the engine room, which keep the engine safe and are never paused. */
export function sharedParts(schedule: Pick<EngineSchedule, "parts"> | null): EnginePart[] {
  return (schedule?.parts ?? []).filter((p) => hubSwitchKey(p.hub) === null);
}

/** "Last run today at 09:12", "Last run yesterday at 18:30", "Last run 12 Oct", "Never run". */
export function lastRunText(iso: string | null | undefined, now = new Date()): string {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return "Never run";
  const day = fmtDayRelative(iso, now);
  return day === "today" || day === "yesterday" ? `Last run ${day} at ${fmtTime(iso)}` : `Last run ${day}`;
}

/**
 * "Next run today at 15:00", "Next run tomorrow at 06:00", "Next run 12 Oct"; "Next run due now" once its time has
 * passed (the engine picks it up within seconds); null when it has no next run (#364).
 */
export function nextRunText(iso: string | null | undefined, now = new Date()): string | null {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return null;
  if (new Date(iso).getTime() <= now.getTime()) return "Next run due now";
  const day = fmtDayRelative(iso, now);
  return day === "today" || day === "tomorrow" ? `Next run ${day} at ${fmtTime(iso)}` : `Next run ${day}`;
}

/** The row's second line: its last run, then its next run or how often it runs when it runs all the time (#364). */
export function runText(r: Pick<AgentRow, "last_run_at" | "next_run_at" | "always" | "word">, now = new Date()): string {
  const last = lastRunText(r.last_run_at, now);
  if (r.word === "Working") return last;
  const next = r.always ?? nextRunText(r.next_run_at, now);
  return next ? `${last} · ${next}` : last;
}
